import * as React from "react";
import { ChangeEvent } from "react";
import DOMPurify from "dompurify";
import "../inputField.scss";
import "./textInputStyle.scss";
import { classNames } from "../../utils";
import { TInputValidator, IValidatorResult } from "../../utils/validators";

const { useState, useRef, useEffect } = React;

let keyCodeStore = 0;
export interface ITextInputProps {
  className?: string;
  style?: React.CSSProperties;
  inputLabel: string;
  inputLabelDisclaimer?: string;
  inputId: string;
  initValue?: string | number;
  forceUpdate?: { value: string | number };
  validators?: TInputValidator[];
  upperCase?: boolean;
  onValidation?: IValidationOutputFunction;
  prefix?: (value: string | number) => JSX.Element;
  typingHandler?: (value: string, keyCode?: number, prev?: string) => string;
  cluster?: { [key: string]: (event?: React.ChangeEvent | Event) => boolean };
  disabled?: boolean;
  verified?: boolean;
  description?: string;
  type?: string;
  maxLength?: number;
  max?: string | number;
  min?: string | number;
  pattern?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  placeholder?: string;
  autocomplete?: string;
  onInput?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export type IValidationOutputFunction = (
  result: { value: string; inputId: string } & IValidatorResult
) => void;

const TextInput: React.FC<ITextInputProps> = (props) => {
  const {
    className,
    style,
    inputLabel,
    inputLabelDisclaimer = "",
    inputId,
    initValue,
    forceUpdate,
    validators,
    upperCase,
    onValidation,
    prefix,
    typingHandler,
    cluster,
    disabled,
    verified,
    description,
    type = "text",
    maxLength = 100,
    max,
    min,
    pattern,
    inputMode,
    placeholder,
    autocomplete,
    onInput,
  } = props;

  const inputElem = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isValid, setIsValid] = useState<boolean>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [value, setValue] = useState<string | number>(initValue || "");

  useEffect(() => {
    if (cluster) {
      cluster[inputId] = onChangeHandler;
    }
    return () => {
      if (cluster && cluster[inputId]) {
        delete cluster[inputId];
      }
    };
  });

  useEffect(() => {
    if (forceUpdate && forceUpdate.value !== undefined) {
      setValue(forceUpdate.value);
      // setIsValid(true)
      // setErrorMessage("")
    }
     
  }, [forceUpdate]);

  useEffect(() => {
    if (initValue || initValue === "") {
      setValue(initValue);
    }
    // setIsValid(true)
    // eslint-disable-next-line
  }, []);

  const onChangeHandler = (event?: React.ChangeEvent | Event) => {
    if (!inputElem.current) return;

    let newValue = inputElem.current.value;
    if (!!typingHandler) {
      newValue = typingHandler(newValue, keyCodeStore, value as string);
    }

    if (upperCase) newValue = newValue.toUpperCase();

    if (newValue === "") setIsValid(null);
    setValue(newValue);
    // inputElem.current.value = newValue

    const validation = validate({ validators, value: newValue });
    if (!validation) {
      return false;
    } else {
      if (onValidation) {
        onValidation({ ...validation, value: newValue, inputId });
      }
    }

    if (validation) {
      /** validation failure */
      if (!event || validation.isValid === true) {
        setIsValid(validation.isValid);
        setErrorMessage(validation.message || "");
      }
      if (newValue === "" && validation.isValid === true) {
        setIsValid(null);
      }
      return validation.isValid === true;
    }

    return false;
  };

  const onFocus = () => {
    setIsFocused(true);
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const preventNumberE = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key && event.key.toLowerCase() === "e") {
      event.preventDefault();
    }
  };

  const preventDecimalNumber = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key && event.key === ".") {
      event.preventDefault();
    }
  };

  const isActive = isValid === true || isFocused || verified || value !== "";
  return (
    <div className={className} style={style}>
      <div
        className={classNames({
          inputField: true,
          "inputField--warning": isValid === false,
          "inputField--withDescription": !!description,
          textInput: true,
          "textInput--active": isActive,
        })}
        onClick={() => inputElem.current && inputElem.current.focus()}
      >
        <label
          data-testid="label"
          className={classNames({
            inputField__label: true,
            "inputField__label--onTop": isActive,
          })}
        >
          {inputLabelDisclaimer && (
            <>
              {inputLabelDisclaimer}
              <br />
            </>
          )}
          {`${inputLabel} ${errorMessage}`}
        </label>
        <div className="textInput__content">
          {prefix && <div className="textInput__prefix">{prefix(value)}</div>}
          <input
            className="textInput__input"
            type={type}
            maxLength={maxLength}
            max={max}
            min={min}
            name={inputId}
            data-name={inputId}
            ref={inputElem}
            onInput={onInput}
            onChange={onChangeHandler}
            onFocus={onFocus}
            onBlur={onBlur}
            value={value}
            disabled={disabled}
            pattern={pattern}
            inputMode={inputMode}
            autoComplete={autocomplete}
            onKeyDown={(event) => {
              if (type === "number") preventNumberE(event);
              if (type === "number") preventDecimalNumber(event);
              keyCodeStore = event.keyCode;
            }}
            placeholder={placeholder}
          />
        </div>

        <div
          className={classNames({
            inputField__icon: true,
            "inputField__icon--hidden":
              isValid !== true && isValid !== false && verified !== true,
            "inputField__icon--isValid": isValid === true,
            "inputField__icon--isVerified": verified === true,
            "inputField__icon--isInvalid": isValid === false,
          })}
        />
      </div>

      {description && (
        <div
          className={"inputField__description"}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        ></div>
      )}
    </div>
  );
};

interface IValidateInput {
  validators: ITextInputProps["validators"];
  value: string;
}

const validate = ({ validators, value }: IValidateInput) => {
  if (Array.isArray(validators)) {
    for (const validator of validators) {
      const result = validator(value);
      if (!result.isValid) {
        return result;
      }
    }
  }

  return { isValid: true, message: "" };
};

export default TextInput;